import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  financeStructureApi,
  formatAgorot,
  shekelsToAgorot,
  type FinanceFund,
  type FundTransfer,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";

const PRESET_COLORS = [
  "#4caf50",
  "#2196f3",
  "#f44336",
  "#ff9800",
  "#9c27b0",
  "#00bcd4",
  "#795548",
  "#607d8b",
  "#e91e63",
  "#3f51b5",
];

type Props = { writeCustomerId: string };

export function FundsTab({ writeCustomerId }: Props) {
  const [rows, setRows] = useState<FinanceFund[]>([]);
  const [transfers, setTransfers] = useState<FundTransfer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceFund | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);

  const [fromFundId, setFromFundId] = useState("");
  const [toFundId, setToFundId] = useState("");
  const [amount, setAmount] = useState("");
  const [transferDate, setTransferDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [reference, setReference] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [funds, tr] = await Promise.all([
        financeStructureApi.listFunds(),
        financeStructureApi.listFundTransfers(),
      ]);
      setRows(funds);
      setTransfers(tr);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const fundName = useMemo(() => {
    const map = new Map(rows.map((r) => [r.id, r.name]));
    return (id: string) => map.get(id) ?? id;
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setColor(null);
    setIsDefault(false);
    setDialogOpen(true);
  };

  const openEdit = (row: FinanceFund) => {
    setEditing(row);
    setName(row.name);
    setDescription(row.description ?? "");
    setColor(row.color);
    setIsDefault(row.isDefault);
    setDialogOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 160,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as FinanceFund;
          return (
            <span>
              {r.color ? (
                <span className="fs-color-swatch" style={{ background: r.color }} />
              ) : null}
              {r.name}
              {r.isDefault ? <span className="fs-chip fs-chip--default">ברירת מחדל</span> : null}
              {r.isSystem ? <span className="fs-chip fs-chip--system">מערכת</span> : null}
            </span>
          );
        },
      },
      {
        key: "balance",
        title: "יתרה",
        sortable: true,
        minWidth: 100,
        render: (_value: unknown, row: Record<string, unknown>) => formatAgorot(Number(row.balance ?? 0)),
      },
      {
        key: "usageCount",
        title: "שימושים",
        sortable: true,
        minWidth: 80,
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 180,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as FinanceFund;
          if (r.isSystem) {
            return (
              <TableRowActions>
                {!r.isDefault ? (
                  <QuietButton
                    type="button"
                    onClick={() =>
                      void financeStructureApi
                        .setDefaultFund(r.customerId, r.id)
                        .then(load)
                        .catch((e) => setError(toClientApiError(e).message))
                    }
                  >
                    ברירת מחדל
                  </QuietButton>
                ) : null}
              </TableRowActions>
            );
          }
          return (
            <TableRowActions>
              <QuietButton type="button" onClick={() => openEdit(r)}>
                עריכה
              </QuietButton>
              {!r.isDefault ? (
                <QuietButton
                  type="button"
                  onClick={() =>
                    void financeStructureApi
                      .setDefaultFund(r.customerId, r.id)
                      .then(load)
                      .catch((e) => setError(toClientApiError(e).message))
                  }
                >
                  ברירת מחדל
                </QuietButton>
              ) : null}
              <QuietButton type="button" onClick={() => setDeleteId(r.id)}>
                מחיקה
              </QuietButton>
            </TableRowActions>
          );
        },
      },
    ],
    [load],
  );

  const transferColumns = useMemo(
    () => [
      {
        key: "transferDate",
        title: "תאריך",
        sortable: true,
        render: (_value: unknown, row: Record<string, unknown>) =>
          new Date(String(row.transferDate)).toLocaleDateString("he-IL"),
      },
      {
        key: "fromFundId",
        title: "מקופה",
        render: (_value: unknown, row: Record<string, unknown>) => fundName(String(row.fromFundId)),
      },
      {
        key: "toFundId",
        title: "לקופה",
        render: (_value: unknown, row: Record<string, unknown>) => fundName(String(row.toFundId)),
      },
      {
        key: "amountAgorot",
        title: "סכום",
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.amountAgorot ?? 0)),
      },
      {
        key: "actions",
        title: "",
        render: (_value: unknown, row: Record<string, unknown>) => (
          <QuietButton
            type="button"
            onClick={() => {
              const t = row as unknown as FundTransfer;
              void financeStructureApi
                .deleteFundTransfer(t.customerId, t.id)
                .then(load)
                .catch((e) => setError(toClientApiError(e).message));
            }}
          >
            מחיקה
          </QuietButton>
        ),
      },
    ],
    [fundName, load],
  );

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="קופה חדשה"
        onPrimary={openCreate}
        secondary={
          <SettingsButton
            type="button"
            variant="settings"
            size="sm"
            onClick={() => {
              setFromFundId(rows[0]?.id ?? "");
              setToFundId(rows[1]?.id ?? rows[0]?.id ?? "");
              setAmount("");
              setReference("");
              setTransferOpen(true);
            }}
          >
            העברה בין קופות
          </SettingsButton>
        }
      />
      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p>טוען…</p> : null}
      {!loading ? (
        <>
          <TableKit
            columns={columns}
            rows={rows as unknown as Record<string, unknown>[]}
            rowKey="id"
            emptyMessage="אין קופות"
          />
          <h3 style={{ marginTop: "1.5rem" }}>העברות</h3>
          <TableKit
            columns={transferColumns}
            rows={transfers as unknown as Record<string, unknown>[]}
            rowKey="id"
            emptyMessage="אין העברות"
          />
        </>
      ) : null}

      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="md">
        <FormCard
          title={editing ? "עריכת קופה" : "קופה חדשה"}
          onSave={() => {
            void (async () => {
              setSaving(true);
              try {
                const body = {
                  name: name.trim(),
                  description: description.trim() || null,
                  color,
                  isDefault,
                };
                if (editing) {
                  await financeStructureApi.updateFund(editing.customerId, editing.id, body);
                } else {
                  await financeStructureApi.createFund(writeCustomerId, body);
                }
                setDialogOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !name.trim()}
        >
          <FormSection title="פרטים">
            <FormGrid>
              <FormField
                label="שם"
                required
                value={name}
                onChange={setName}
                disabled={Boolean(editing?.isSystem)}
              />
              <FormField
                label="תיאור"
                value={description}
                onChange={setDescription}
              />
              <FormField label="צבע">
                <div className="fs-presets">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`fs-presets__swatch${color === c ? " fs-presets__swatch--selected" : ""}`}
                      style={{ background: c }}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              </FormField>
              <FormField
                checkbox
                label="קופת ברירת מחדל"
                checked={isDefault}
                onChange={
                  ((v: boolean) => setIsDefault(v)) as unknown as (
                    value: string,
                  ) => void
                }
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <FormDialog open={transferOpen} onClose={() => setTransferOpen(false)} size="md">
        <FormCard
          title="העברה בין קופות"
          onSave={() => {
            void (async () => {
              setSaving(true);
              try {
                const from = rows.find((r) => r.id === fromFundId);
                const to = rows.find((r) => r.id === toFundId);
                if (!from || !to || from.customerId !== to.customerId) {
                  throw new Error("ניתן להעביר רק בין קופות של אותו לקוח");
                }
                await financeStructureApi.createFundTransfer(from.customerId, {
                  fromFundId,
                  toFundId,
                  amountAgorot: shekelsToAgorot(Number(amount)),
                  transferDate: new Date(transferDate).toISOString(),
                  reference: reference.trim() || null,
                });
                setTransferOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setTransferOpen(false)}
          saveLabel="העברה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={
            saving || !fromFundId || !toFundId || !amount || Number(amount) <= 0
          }
        >
          <FormSection title="העברה">
            <FormGrid>
              <FormField
                label="מקופה"
                required
                value={fromFundId}
                onChange={setFromFundId}
                options={rows.map((r) => ({ value: r.id, label: r.name }))}
              />
              <FormField
                label="לקופה"
                required
                value={toFundId}
                onChange={setToFundId}
                options={rows.map((r) => ({ value: r.id, label: r.name }))}
              />
              <FormField
                label="סכום (₪)"
                required
                type="number"
                value={amount}
                onChange={setAmount}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField
                label="תאריך"
                required
                type="date"
                value={transferDate}
                onChange={setTransferDate}
              />
              <FormField
                label="אסמכתא"
                value={reference}
                onChange={setReference}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        title="מחיקת קופה"
        message="רשומות מקושרות יועברו לקופת המערכת. להמשיך?"
        confirmText="מחיקה"
        onConfirm={() => {
          if (!deleteId) return;
          const fund = rows.find((r) => r.id === deleteId);
          if (!fund) return;
          void financeStructureApi
            .deleteFund(fund.customerId, deleteId)
            .then(() => {
              setDeleteId(null);
              return load();
            })
            .catch((e) => setError(toClientApiError(e).message));
        }}
      />
    </div>
  );
}
